/**
 * L.11 — PromptLab inline specialization selector.
 *
 * Reads `lorasByPortal.listForPortal` and surfaces every registered
 * specialization the user could attach to this session. Selecting one
 * pins it for the session; "(none)" reverts to pure base-model output.
 *
 * D2 (post-handoff): when a row is selected, the parent PromptLabWindow
 * now routes Run through `inferenceJobs.request({loraUrl})` instead of
 * the placeholder status flip. The selector therefore lifts the full
 * resolved row (tinkerLoraUrl + baseModel) up via `onSelect` so the
 * parent doesn't need a second lorasByPortal subscription.
 */
import { useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';

export interface SpecializationSelection {
  loraRowId: string;
  tinkerLoraUrl: string;
  baseModel: string;
  loraRank: number;
}

interface SpecializationSelectorProps {
  selectedLoraRowId: string | null;
  onSelect: (selection: SpecializationSelection | null) => void;
}

interface LoraRow {
  _id: string;
  tinkerLoraUrl: string;
  baseModel: string;
  loraRank: number;
  sourceRunRowId: string;
  status?: string;
  /**
   * 2026-05-27 — operator/trainer-authored label. When present, the
   * dropdown renders this instead of the raw `baseModel · rank N`
   * string. Synthesized from `tinkerSpikeRuns.skillNamesUsedJson` in
   * the callback when the trainer doesn't author one explicitly.
   */
  displayName?: string;
  description?: string;
}

/**
 * Synthesize a label when the row pre-dates the `displayName` field
 * (legacy rows, smoke fixtures, demo rows). Keeps the technical detail
 * visible so power users / engineers can still tell rows apart, but
 * favors a recognizable left-hand fragment ("Llama 3 · rank 16") over
 * the raw fully-qualified base-model string.
 */
function fallbackLabel(row: LoraRow): string {
  const baseShort = row.baseModel.split('/').pop() ?? row.baseModel;
  return `${baseShort} · rank ${row.loraRank}`;
}

function tooltipFor(row: LoraRow): string {
  if (row.description && row.description.trim().length > 0) {
    return row.description;
  }
  return `Trained on ${row.baseModel}, rank ${row.loraRank}.`;
}

const wrapStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flexShrink: 0,
  fontFamily: 'var(--xp-font-body)',
  fontSize: 11,
  color: '#000',
};

const selectStyle: React.CSSProperties = {
  fontFamily: 'var(--xp-font-body)',
  fontSize: 11,
  padding: '2px 4px',
  background: '#fff',
  color: '#000',
  borderStyle: 'solid',
  borderWidth: 2,
  borderTopColor: '#808080',
  borderLeftColor: '#808080',
  borderBottomColor: '#dfdfdf',
  borderRightColor: '#dfdfdf',
  maxWidth: 220,
};

export function SpecializationSelector({
  selectedLoraRowId,
  onSelect,
}: SpecializationSelectorProps): React.ReactElement | null {
  // Use the same "default-portal" anchor that tinkerDataset uses for live
  // runs.
  const portalSlug = 'default-portal';
  const loras = useQuery(
    api.lorasByPortal.listForPortal,
    { portalSlug, limit: 20 },
  ) as LoraRow[] | undefined;

  const options = useMemo<LoraRow[]>(
    () =>
      (loras ?? []).filter(
        (l) => l.status === undefined || l.status === 'active',
      ),
    [loras],
  );

  if (loras === undefined) {
    return (
      <span style={wrapStyle} data-testid={testIds.promptLab.specializationLoading}>
        Specialization: <em>loading…</em>
      </span>
    );
  }
  if (options.length === 0) {
    return (
      <span
        style={wrapStyle}
        data-testid={testIds.promptLab.specializationEmpty}
        title="No specializations trained yet. Specialize a skill from the Skill Library to see options here."
      >
        Specialization: <em>none trained yet</em>
      </span>
    );
  }

  return (
    <label style={wrapStyle}>
      <span className="font-bold">Specialization:</span>
      <select
        data-testid={testIds.promptLab.specializationSelect}
        value={selectedLoraRowId ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '') {
            onSelect(null);
            return;
          }
          const row = options.find((r) => r._id === v);
          if (!row) {
            onSelect(null);
            return;
          }
          onSelect({
            loraRowId: row._id,
            tinkerLoraUrl: row.tinkerLoraUrl,
            baseModel: row.baseModel,
            loraRank: row.loraRank,
          });
        }}
        style={selectStyle}
      >
        <option value="">(none)</option>
        {options.map((row) => {
          const label =
            row.displayName && row.displayName.trim().length > 0
              ? row.displayName
              : fallbackLabel(row);
          return (
            <option key={row._id} value={row._id} title={tooltipFor(row)}>
              {label}
            </option>
          );
        })}
      </select>
    </label>
  );
}
